
import React, { useState, useEffect } from 'react';

interface FamilyStepProps {
  t: any;
  isRTL: boolean;
  language: string;
  onBack: () => void;
  onHome: () => void;
  onContinue: (specialNeeds: boolean, size: string, privateSchools: boolean) => void;
}

const FamilyStep: React.FC<FamilyStepProps> = ({ t, isRTL, language, onBack, onHome, onContinue }) => {
  const [familySize, setFamilySize] = useState<string>('');
  const [privateSchools, setPrivateSchools] = useState(false);
  const [specialNeeds, setSpecialNeeds] = useState(false);

  useEffect(() => {
    if (familySize === 'no_children') {
      setPrivateSchools(false);
      setSpecialNeeds(false);
    }
  }, [familySize]);

  const isTogglesDisabled = familySize !== 'family_with_children';

  const options = [
    { id: 'family_with_children', label: t.familyWithChildren, icon: 'diversity_1' },
    { id: 'no_children', label: t.noChildren, icon: 'person' },
  ];

  return (
    <div className={`relative flex flex-col h-screen w-full max-w-2xl mx-auto font-display ${isRTL ? 'text-right' : 'text-left'}`}>
      <header className={`relative w-full pt-6 md:pt-10 px-8 md:px-12 pb-2 md:pb-4 flex items-center justify-between z-20`}>
        <button 
          onClick={onHome}
          className={`flex items-center gap-2.5 transition-opacity hover:opacity-80 active:scale-95 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
        >
          <span className="material-symbols-outlined text-[#0066FF] text-[26px] md:text-[30px] font-bold">flight_takeoff</span>
          <h2 className="text-slate-dark tracking-tighter text-[20px] md:text-[22px] font-black leading-none">Settlo</h2>
        </button>
        <button 
          onClick={onBack}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100 text-slate-soft hover:text-primary transition-all active:scale-95 group"
        >
          <span className="text-[12px] font-bold uppercase tracking-[0.1em]">{language === 'zh' ? '返回' : 'Back'}</span>
          <span className={`material-symbols-outlined text-[18px] ${isRTL ? '' : 'rotate-180'}`}>arrow_forward</span>
        </button>
      </header>

      <main className="px-8 md:px-12 pb-2 flex flex-col flex-grow pt-4 md:pt-8 overflow-y-auto no-scrollbar">
        <div className={`mb-6 md:mb-10 flex flex-col ${isRTL ? 'items-end' : 'items-start'}`}>
          <div className="text-slate-soft/60 text-[11px] font-black tracking-[0.3em] uppercase mb-3">
            {t.step}
          </div>
          <h1 className="text-[28px] md:text-[42px] font-extrabold tracking-tight leading-[1.1] text-slate-dark mb-4">
            {t.title}
          </h1>
          <p className="text-slate-soft text-[16px] md:text-[18px] leading-relaxed max-w-lg">
            {t.subtitle}
          </p>
        </div>

        <section className="flex flex-col gap-6 md:gap-8">
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {options.map((option) => (
              <button
                key={option.id}
                onClick={() => setFamilySize(option.id)}
                className={`group relative flex flex-col items-center gap-3 p-5 md:p-6 rounded-stripe border transition-all duration-300 transform active:scale-[0.98] text-center ${
                  familySize === option.id
                    ? 'bg-primary/5 border-primary shadow-lg shadow-primary/10'
                    : 'bg-white border-[#E6E6E8] shadow-stripe-card'
                }`}
              >
                <div className={`flex-none w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                  familySize === option.id ? 'bg-primary text-white' : 'bg-slate-50 text-slate-soft'
                }`}>
                  <span className="material-symbols-outlined text-[24px] md:text-[28px]" style={{ fontVariantLigatures: 'none' }}>
                    {option.icon}
                  </span>
                </div>
                <span className={`text-[14px] md:text-[16px] font-bold tracking-tight ${
                  familySize === option.id ? 'text-primary' : 'text-slate-dark'
                }`}>
                  {option.label}
                </span>
              </button>
            ))}
          </div>

          <div className={`space-y-3 md:space-y-4 transition-all duration-500 ${isTogglesDisabled ? 'opacity-30 grayscale pointer-events-none' : 'opacity-100'}`}>
            <div className="group flex items-center justify-between p-4 md:p-5 bg-white rounded-stripe border border-[#E6E6E8] shadow-stripe-card">
              <div className={`flex flex-col ${isRTL ? 'pl-2' : 'pr-2'}`}>
                <span className="text-[14px] md:text-[16px] font-bold text-slate-dark tracking-tight">{t.privateSchools}</span>
                <span className="text-[12px] md:text-[14px] text-slate-soft mt-0.5 leading-tight">{t.privateSchoolsSub}</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input checked={privateSchools} onChange={(e) => setPrivateSchools(e.target.checked)} disabled={isTogglesDisabled} className="sr-only peer" type="checkbox" />
                <div className="w-[44px] h-[24px] bg-slate-200 rounded-full peer peer-checked:bg-[#0066FF] after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-[20px] after:w-[20px] after:transition-all peer-checked:after:translate-x-[20px]"></div>
              </label>
            </div>
            <div className="group flex items-center justify-between p-4 md:p-5 bg-white rounded-stripe border border-[#E6E6E8] shadow-stripe-card">
              <div className={`flex flex-col ${isRTL ? 'pl-2' : 'pr-2'}`}>
                <span className="text-[14px] md:text-[16px] font-bold text-slate-dark tracking-tight">{t.specialNeeds}</span>
                <span className="text-[12px] md:text-[14px] text-slate-soft mt-0.5 leading-tight">{t.specialNeedsSub}</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input checked={specialNeeds} onChange={(e) => setSpecialNeeds(e.target.checked)} disabled={isTogglesDisabled} className="sr-only peer" type="checkbox" />
                <div className="w-[44px] h-[24px] bg-slate-200 rounded-full peer peer-checked:bg-[#0066FF] after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-[20px] after:w-[20px] after:transition-all peer-checked:after:translate-x-[20px]"></div>
              </label>
            </div>
          </div>
        </section>
      </main>

      <footer className="w-full px-8 md:px-12 pb-8 md:pb-12 pt-4 shrink-0">
        <button 
          disabled={!familySize}
          onClick={() => familySize && onContinue(specialNeeds, familySize, privateSchools)}
          className={`group w-full relative flex items-center justify-center gap-2.5 font-black text-[17px] py-4 md:py-5 rounded-stripe shadow-stripe-button transition-all duration-300 active:scale-[0.99] ${
            familySize ? 'bg-[#0066FF] text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          <span>{t.continue}</span>
        </button>
      </footer>
    </div>
  );
};

export default FamilyStep;
